import { useCallback, useEffect, useRef, useState } from "react";

export interface PendingRequest<Response> {
  response: Promise<Response>;
  abort: (() => void) | null;
}

export type RequestCompletion<Request, Response> = {
  request: Request;
  attempt: number;
} & ({ status: "success"; response: Response } | { status: "error" });

export const useDebouncedRequest = <Request, Response>({
  request,
  check,
  delay,
}: {
  request: Request | null;
  check: (
    request: Request,
    previous: RequestCompletion<Request, Response> | null
  ) => PendingRequest<Response>;
  delay: number;
}) => {
  const [attempt, setAttempt] = useState(0);
  const [completion, setCompletion] = useState<RequestCompletion<
    Request,
    Response
  > | null>(null);
  const completedRef = useRef<RequestCompletion<Request, Response> | null>(
    null
  );
  const retry = useCallback(() => {
    setAttempt((attempt) => attempt + 1);
  }, []);

  useEffect(() => {
    if (request === null) {
      completedRef.current = null;
      return;
    }
    let cancelled = false;
    let pending: PendingRequest<Response> | undefined;
    const timer = setTimeout(() => {
      pending = check(request, completedRef.current);
      const complete = (completion: RequestCompletion<Request, Response>) => {
        if (cancelled) return;
        completedRef.current = completion;
        setCompletion(completion);
      };
      void pending.response.then(
        (response) => {
          complete({ request, attempt, status: "success", response });
        },
        () => {
          complete({ request, attempt, status: "error" });
        }
      );
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      pending?.abort?.();
    };
  }, [request, check, delay, attempt]);

  const isCurrent =
    completion !== null &&
    completion.request === request &&
    completion.attempt === attempt &&
    completedRef.current === completion;
  return {
    completion,
    isCurrent,
    retry,
    isChecking: request !== null && !isCurrent,
    hasError: request !== null && isCurrent && completion.status === "error",
  };
};
